/* oxlint-disable typescript/no-explicit-any -- attributes/match vars can be any type */
// Process ![image](<src> "title"){attrs}

import type { HTMLAttribute, Token } from '../../../token'
import type StateInline from '../state-inline'
import { isSpace, normalizeReference } from '../../../common/utils'
import { parseAttributeBlock } from '../../helpers/parse-attribute-block'

export default function image(state: StateInline, silent: boolean) {
  let code, content, label, pos, ref, res, title, start
  let href = ''
  let attributes: Record<string, any> = {}
  const oldPos = state.pos
  const max = state.posMax

  if (state.src.charCodeAt(state.pos) !== 0x21 /* ! */) return false
  if (state.src.charCodeAt(state.pos + 1) !== 0x5b /* [ */) return false

  const labelStart = state.pos + 2
  const labelEnd = state.md.helpers.parseLinkLabel(state, state.pos + 1, false)

  // parser failed to find ']', so it's not a valid link
  if (labelEnd < 0) return false

  pos = labelEnd + 1
  if (pos < max && state.src.charCodeAt(pos) === 0x28 /* ( */) {
    //
    // Inline link
    //

    // [link](  <href>  "title"  )
    //        ^^ skipping these spaces
    pos++
    for (; pos < max; pos++) {
      code = state.src.charCodeAt(pos)
      if (!isSpace(code) && code !== 0x0a) break
    }
    if (pos >= max) return false

    // [link](  <href>  "title"  )
    //          ^^^^^^ parsing link destination
    start = pos
    res = state.md.helpers.parseLinkDestination(state.src, pos, state.posMax)
    if (res.ok) {
      href = state.md.normalizeLink(res.str)
      if (state.md.validateLink(href)) {
        pos = res.pos
      } else {
        href = ''
      }
    }

    // [link](  <href>  "title"  )
    //                ^^ skipping these spaces
    start = pos
    for (; pos < max; pos++) {
      code = state.src.charCodeAt(pos)
      if (!isSpace(code) && code !== 0x0a) break
    }

    // [link](  <href>  "title"  )
    //                  ^^^^^^^ parsing link title
    res = state.md.helpers.parseLinkTitle(state.src, pos, state.posMax)
    if (pos < max && start !== pos && res.ok) {
      title = res.str
      pos = res.pos

      // [link](  <href>  "title"  )
      //                         ^^ skipping these spaces
      for (; pos < max; pos++) {
        code = state.src.charCodeAt(pos)
        if (!isSpace(code) && code !== 0x0a) break
      }
    } else {
      title = ''
    }

    if (pos >= max || state.src.charCodeAt(pos) !== 0x29 /* ) */) {
      state.pos = oldPos
      return false
    }
    pos++

    // Parse optional {attributes} after the closing )
    if (pos < max) {
      const attrRes = parseAttributeBlock(state.src, pos, max)
      if (attrRes.ok) {
        attributes = attrRes.attributes
        pos = attrRes.pos
      }
    }
  } else {
    //
    // Link reference
    //
    if (typeof state.env.references === 'undefined') return false

    if (pos < max && state.src.charCodeAt(pos) === 0x5b /* [ */) {
      start = pos + 1
      pos = state.md.helpers.parseLinkLabel(state, pos)
      if (pos >= 0) {
        label = state.src.slice(start, pos++)
      } else {
        pos = labelEnd + 1
      }
    } else {
      pos = labelEnd + 1
    }

    // covers label === '' and label === undefined
    // (collapsed reference link and shortcut reference link respectively)
    if (!label) {
      label = state.src.slice(labelStart, labelEnd)
    }

    ref = state.env.references[normalizeReference(label)]
    if (!ref) {
      state.pos = oldPos
      return false
    }
    href = ref.href
    title = ref.title
  }

  //
  // We found the end of the link, and know for a fact it's a valid link;
  // so all that's left to do is to call tokenizer.
  //
  if (!silent) {
    content = state.src.slice(labelStart, labelEnd)

    const tokens: Token[] = []
    state.md.inline.parse(content, state.md, state.env, tokens)

    const token = state.push('image', 'img', 0)
    const attrs: HTMLAttribute[] = [
      ['src', href],
      ['alt', ''],
    ]
    token.attrs = attrs
    token.children = tokens
    token.content = content

    if (title) {
      attrs.push(['title', title])
    }

    // Add custom attributes from {attrs}
    for (const [key, value] of Object.entries(attributes)) {
      attrs.push([key, String(value)])
    }
  }

  state.pos = pos
  state.posMax = max
  return true
}
